import PropTypes from "prop-types";
import React from "react";

import { Text, textPropTypes } from "@swa-ui/core";
import i18n from "@swa-ui/locale";
import { classNames } from "@swa-ui/string";

import styles from "./FlightDuration.module.scss";

const MINUTES_PER_HOUR = 60;

/**
 * FlightDuration displays flight duration information.
 *
 * Outputs when duration is 211 minutes:
 * Without separator: 3h31m
 * With separator: 3h 31m
 */
export const FlightDuration = (props) => {
  const { className, duration, separator = " ", textProps } = props;

  return <Text {...getProps()}>{renderFormattedDuration()}</Text>;

  function getProps() {
    return {
      "aria-label": getAriaLabel(),
      className: classNames(styles.duration, className),
      ...textProps,
    };
  }

  function renderFormattedDuration() {
    const { hours, minutes } = getHoursMinutes();
    const formattedHours = i18n("FlightDuration__HOURS_ABBREVIATION", { hours });
    const formattedMinutes = i18n("FlightDuration__MINUTES_ABBREVIATION", { minutes });

    return (
      <>
        {formattedHours}
        {separator}
        {formattedMinutes}
      </>
    );
  }

  function getAriaLabel() {
    let ariaLabel = getDurationAriaLabel();

    if (props["aria-label"]) {
      ariaLabel = `${props["aria-label"]} ${ariaLabel}.`;
    }

    return ariaLabel;
  }

  function getDurationAriaLabel() {
    const { hours, minutes } = getHoursMinutes();
    const formattedHours = i18n("FlightDuration__HOURS_ARIA_LABEL", { hours });
    const formattedMinutes = i18n("FlightDuration__MINUTES_ARIA_LABEL", { minutes });

    return `${formattedHours} ${formattedMinutes}`;
  }

  function getHoursMinutes() {
    return {
      hours: Math.floor(duration / MINUTES_PER_HOUR),
      minutes: duration % MINUTES_PER_HOUR,
    };
  }
};

FlightDuration.propTypes = {
  /** aria-label text to provide accessibility description. */
  "aria-label": PropTypes.string,

  /** Additional class to be added to the outermost element for positioning the component. */
  className: PropTypes.string,

  /** Length in total minutes. */
  duration: PropTypes.number.isRequired,

  /** Indicates if there is a space between hours abbreviation and number of minutes. */
  separator: PropTypes.node,

  /** Props passed to the underlying Text component. See Text for more details. */
  textProps: PropTypes.shape(textPropTypes),
};
